'use client';

import React, { useState, useMemo, useTransition } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import useSWR, { mutate } from 'swr';
import {
  MessageSquare,
  ShoppingBag,
  Users,
  User,
  Settings,
  LogOut,
  Volume2,
  Globe,
  Kanban,
  Zap,
  FileText,
  Megaphone,
  ShieldAlert,
  Archive,
  ChevronRight,
  X,
  Lock,
  Smile,
  Bot,
  Moon,
  CheckCircle2,
  Activity,
} from 'lucide-react';
import { StatusModal } from '@/components/presence/StatusModal';
import { toast } from 'sonner';
import { useLocale } from 'next-intl';
import { useRouter as useI18nRouter, usePathname as useI18nPathname } from '@/i18n/routing';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ThemeSwitcher } from '@/components/theme-switcher';
import { signOut } from '@/app/[locale]/(login)/actions';
import { User as UserType } from '@/lib/db/schema';
import { useInstanceStatus } from '@/lib/hooks/useInstanceStatus';
import { hasPermission, MemberPermissions } from '@/lib/permissions';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  playAlertRing,
  getSelectedSound,
  setSelectedSound as setStoreSelectedSound,
  getAlertRingEnabled,
  setAlertRingEnabled,
  NOTIFICATION_SOUNDS,
} from '@/lib/sound';
import { isMessagingWindowExpired } from '@/lib/chat-window';
import Logo from '@/components/interface/Logo';
import { PwaInstallButton } from '@/components/pwa/PwaInstallPrompt';

const fetcher = (url: string) => fetch(url).then((res) => res.json());

export function MobileBottomNav() {
  const pathname = usePathname();
  const router = useRouter();
  const pathnameWithoutLocale = pathname.replace(/^\/[a-z]{2}(?=\/)/, '');

  // In WhatsApp mobile, when actively in a chat conversation, hide bottom bar
  // so the message input and virtual keyboard have full viewport space
  const isInChatConversation = pathname.includes('/dashboard/chat/');

  const { data: user } = useSWR<UserType>('/api/user', fetcher);
  const { data: chats } = useSWR<any[]>('/api/chats', fetcher);
  const { data: membership } = useSWR<{ role: string; teamId: number; permissions: MemberPermissions }>(
    '/api/team/membership',
    fetcher
  );
  const { hasInstance } = useInstanceStatus();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedSound, setSelectedSound] = useState('sound1');
  const [alertRingEnabled, setAlertRingEnabledState] = useState(true);

  const handleTogglePresence = async () => {
    const nextStatus = (user as any)?.presenceStatus === 'away' ? 'active' : 'away';
    try {
      const res = await fetch('/api/user/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presenceStatus: nextStatus,
          aiHandleChats: nextStatus === 'away' ? true : false,
        }),
      });
      if (res.ok) {
        await mutate('/api/user');
        await mutate('/api/user/presence');
        toast.success(
          nextStatus === 'away'
            ? 'Set as Away. AI agent will handle your chats.'
            : 'Set as Active. You will handle your chats.'
        );
      }
    } catch {}
  };

  const isOwner = membership?.role === 'owner';
  const roleName = membership?.role ? membership.role.charAt(0).toUpperCase() + membership.role.slice(1) : 'Member';

  const hasPerm = (resource: any) => {
    if (!membership) return true;
    if (isOwner) return true;
    return hasPermission(membership.role, membership.permissions, resource);
  };

  React.useEffect(() => {
    setSelectedSound(getSelectedSound());
    setAlertRingEnabledState(getAlertRingEnabled());

    const handleSoundChange = (e: any) => {
      if (e?.detail) setSelectedSound(String(e.detail));
    };
    const handleRingEnabledChange = (e: any) => {
      if (typeof e?.detail === 'boolean') setAlertRingEnabledState(e.detail);
    };

    window.addEventListener('selectedSoundChange', handleSoundChange);
    window.addEventListener('alertRingEnabledChange', handleRingEnabledChange);
    return () => {
      window.removeEventListener('selectedSoundChange', handleSoundChange);
      window.removeEventListener('alertRingEnabledChange', handleRingEnabledChange);
    };
  }, []);

  const totalUnread = useMemo(() => {
    if (!Array.isArray(chats)) return 0;
    let unread = 0;
    for (const c of chats) {
      if (!isMessagingWindowExpired(c)) {
        unread += c.unreadCount || 0;
      }
    }
    return unread;
  }, [chats]);

  async function handleSignOut() {
    await signOut();
    mutate('/api/user');
    router.push('/');
  }

  const locale = useLocale();
  const i18nRouter = useI18nRouter();
  const i18nPathname = useI18nPathname();
  const [, startLanguageTransition] = useTransition();

  const handleLanguageChange = (nextLocale: string) => {
    startLanguageTransition(() => {
      i18nRouter.replace(i18nPathname, { locale: nextLocale });
    });
  };

  const handleToneChange = (toneId: string) => {
    setSelectedSound(toneId);
    setStoreSelectedSound(toneId);
    playAlertRing(toneId, true);
  };

  const handleToggleRing = (checked: boolean) => {
    setAlertRingEnabledState(checked);
    setAlertRingEnabled(checked);
  };

  // If inside chat conversation on mobile, do not render bottom nav
  if (isInChatConversation) {
    return null;
  }

  const isChatsActive =
    pathnameWithoutLocale === '/dashboard' ||
    pathnameWithoutLocale === '/dashboard/' ||
    pathnameWithoutLocale.startsWith('/dashboard/chat');
  const isOrdersActive = pathnameWithoutLocale.startsWith('/orders');
  const isContactsActive = pathnameWithoutLocale.startsWith('/contacts');
  const isProfileActive = isProfileOpen || pathnameWithoutLocale.startsWith('/settings');

  return (
    <>
      <nav
        aria-label="Mobile navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card/95 dark:bg-[#111b21]/95 backdrop-blur-lg border-t border-border/60 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-4 h-16 max-w-lg mx-auto items-center px-1">
          {/* 1. Chats */}
          <Link
            href="/dashboard"
            className={`flex flex-col items-center justify-center py-1 rounded-xl transition-all ${
              isChatsActive
                ? 'text-[#00a884] dark:text-[#25d366]'
                : 'text-[#54656f] dark:text-[#8696a0] hover:text-foreground'
            }`}
          >
            <div className="relative">
              <div
                className={`p-1 rounded-full transition-transform ${
                  isChatsActive ? 'scale-110' : ''
                }`}
              >
                <MessageSquare className="size-5.5 stroke-[2.2]" />
              </div>
              {totalUnread > 0 && (
                <span className="absolute -top-1 -right-2 bg-[#25d366] text-white text-[10px] font-bold px-1.5 py-0.2 rounded-full min-w-[18px] h-4.5 flex items-center justify-center shadow-xs">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </div>
            <span
              className={`text-[11px] mt-0.5 tracking-tight ${
                isChatsActive ? 'font-bold' : 'font-medium'
              }`}
            >
              Chats
            </span>
          </Link>

          {/* 2. Orders */}
          <Link
            href="/orders"
            className={`flex flex-col items-center justify-center py-1 rounded-xl transition-all ${
              isOrdersActive
                ? 'text-[#00a884] dark:text-[#25d366]'
                : 'text-[#54656f] dark:text-[#8696a0] hover:text-foreground'
            }`}
          >
            <div
              className={`p-1 rounded-full transition-transform ${
                isOrdersActive ? 'scale-110' : ''
              }`}
            >
              <ShoppingBag className="size-5.5 stroke-[2.2]" />
            </div>
            <span
              className={`text-[11px] mt-0.5 tracking-tight ${
                isOrdersActive ? 'font-bold' : 'font-medium'
              }`}
            >
              Orders
            </span>
          </Link>

          {/* 3. Contacts */}
          <Link
            href="/contacts"
            className={`flex flex-col items-center justify-center py-1 rounded-xl transition-all ${
              isContactsActive
                ? 'text-[#00a884] dark:text-[#25d366]'
                : 'text-[#54656f] dark:text-[#8696a0] hover:text-foreground'
            }`}
          >
            <div
              className={`p-1 rounded-full transition-transform ${
                isContactsActive ? 'scale-110' : ''
              }`}
            >
              <Users className="size-5.5 stroke-[2.2]" />
            </div>
            <span
              className={`text-[11px] mt-0.5 tracking-tight ${
                isContactsActive ? 'font-bold' : 'font-medium'
              }`}
            >
              Contacts
            </span>
          </Link>

          {/* 4. Profile / Menu */}
          <button
            type="button"
            onClick={() => setIsProfileOpen(true)}
            className={`flex flex-col items-center justify-center py-1 rounded-xl transition-all cursor-pointer ${
              isProfileActive
                ? 'text-[#00a884] dark:text-[#25d366]'
                : 'text-[#54656f] dark:text-[#8696a0] hover:text-foreground'
            }`}
          >
            <div className="relative">
              <Avatar
                className={`size-6.5 transition-all ${
                  isProfileActive
                    ? 'ring-2 ring-[#00a884] dark:ring-[#25d366] scale-105'
                    : 'ring-1 ring-border'
                }`}
              >
                <AvatarImage
                  src={(user as any)?.avatarUrl || ''}
                  alt={user?.name || user?.email || 'User'}
                />
                <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold">
                  {(user?.name || user?.email || 'U').substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>
            <span
              className={`text-[11px] mt-0.5 tracking-tight ${
                isProfileActive ? 'font-bold' : 'font-medium'
              }`}
            >
              Profile
            </span>
          </button>
        </div>
      </nav>

      {/* Profile & Quick Navigation Bottom Sheet */}
      <Sheet open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl max-h-[85vh] overflow-y-auto p-0 border-t border-border/80 bg-card shadow-2xl"
        >
          <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full mx-auto my-3" />

          {/* User Profile Header */}
          <div className="px-6 pb-4 pt-1 flex items-center gap-4 border-b border-border/50">
            <div className="relative">
              <Avatar className="size-14 ring-2 ring-primary/20">
                <AvatarImage
                  src={(user as any)?.avatarUrl || ''}
                  alt={user?.name || 'User'}
                />
                <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                  {(user?.name || user?.email || 'U').substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {(user as any)?.customStatusEmoji ? (
                <span className="absolute -bottom-1 -right-1 size-5 rounded-full bg-background border border-border flex items-center justify-center text-xs shadow-xs">
                  {(user as any).customStatusEmoji}
                </span>
              ) : (user as any)?.presenceStatus === 'away' ? (
                <span className="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full border-2 border-background bg-amber-500" title="Away" />
              ) : (
                <span className="absolute -bottom-0.5 -right-0.5 size-3.5 rounded-full border-2 border-background bg-emerald-500" title="Active" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-base text-foreground truncate">
                {user?.name || 'User'}
              </h3>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                  {user?.role || 'Member'}
                </span>
                {membership?.role && (
                  <span className="text-[10px] text-muted-foreground">
                    Team: {roleName}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Slack-style Status Card */}
          <div className="mx-4 mt-3 p-3 rounded-2xl bg-muted/40 border border-border/50 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {(user as any)?.presenceStatus === 'away' ? (
                  <div className="flex items-center gap-1.5 text-xs font-bold text-amber-600 dark:text-amber-400">
                    <span className="size-2 rounded-full border-2 border-amber-500 bg-transparent" />
                    <span>Away</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                    <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span>Active</span>
                  </div>
                )}
                {(user as any)?.customStatusEmoji && (
                  <span className="text-sm">{(user as any).customStatusEmoji}</span>
                )}
              </div>

              {(user as any)?.aiHandleChats && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                  <Bot className="size-3" /> AI Taking Over
                </span>
              )}
            </div>

            {(user as any)?.customStatusText && (
              <p className="text-xs text-muted-foreground italic">
                &ldquo;{(user as any).customStatusText}&rdquo;
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 pt-1 border-t border-border/40">
              <button
                type="button"
                onClick={handleTogglePresence}
                className="flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl bg-background border border-border text-xs font-medium hover:bg-muted transition-colors"
              >
                {(user as any)?.presenceStatus === 'away' ? (
                  <>
                    <CheckCircle2 className="size-3.5 text-emerald-500" />
                    <span>Set Active</span>
                  </>
                ) : (
                  <>
                    <Moon className="size-3.5 text-amber-500" />
                    <span>Set Away</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => setIsStatusModalOpen(true)}
                className="flex items-center justify-center gap-1.5 py-2 px-2.5 rounded-xl bg-background border border-border text-xs font-medium hover:bg-muted transition-colors"
              >
                <Smile className="size-3.5 text-blue-500" />
                <span>Update status</span>
              </button>
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* PWA Install Button */}
            <PwaInstallButton />

            {/* Quick Links Group */}
            <div className="space-y-1 bg-muted/30 p-2 rounded-2xl border border-border/40">
              <Link
                href="/settings/general"
                onClick={() => setIsProfileOpen(false)}
                className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/70 transition-colors text-sm font-medium text-foreground"
              >
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <User className="size-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Account & Profile</div>
                    <div className="text-[11px] text-muted-foreground">Manage details and signature</div>
                  </div>
                </div>
                <ChevronRight className="size-4 text-muted-foreground" />
              </Link>

              <Link
                href="/settings"
                onClick={() => setIsProfileOpen(false)}
                className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/70 transition-colors text-sm font-medium text-foreground"
              >
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <Settings className="size-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">All Settings</div>
                    <div className="text-[11px] text-muted-foreground">Team, WhatsApp API, AI & Security</div>
                  </div>
                </div>
                <ChevronRight className="size-4 text-muted-foreground" />
              </Link>
            </div>

            {/* Additional CRM Features */}
            <div className="space-y-1">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-2">
                Features
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  href="/pipeline"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/40 hover:bg-muted/80 border border-border/40 transition-colors"
                >
                  <Kanban className="size-4 text-amber-500 shrink-0" />
                  <span className="text-xs font-semibold text-foreground">Sales Pipeline</span>
                </Link>

                {user?.role === 'owner' && (
                  <Link
                    href="/monitoring"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-2.5 p-3 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors"
                  >
                    <Activity className="size-4 text-emerald-500 shrink-0" />
                    <span className="text-xs font-semibold text-foreground">Team Monitoring</span>
                  </Link>
                )}

                <Link
                  href="/automation"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/40 hover:bg-muted/80 border border-border/40 transition-colors"
                >
                  <Zap className="size-4 text-purple-500 shrink-0" />
                  <span className="text-xs font-semibold text-foreground">Automation</span>
                </Link>

                {user?.role === 'owner' && (
                  <Link
                    href="/templates"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/40 hover:bg-muted/80 border border-border/40 transition-colors"
                  >
                    <FileText className="size-4 text-emerald-500 shrink-0" />
                    <span className="text-xs font-semibold text-foreground">WhatsApp Templates</span>
                  </Link>
                )}

                <Link
                  href="/campaigns"
                  onClick={() => setIsProfileOpen(false)}
                  className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/40 hover:bg-muted/80 border border-border/40 transition-colors"
                >
                  <Megaphone className="size-4 text-orange-500 shrink-0" />
                  <span className="text-xs font-semibold text-foreground">Campaigns</span>
                </Link>

                {user?.role === 'admin' && (
                  <Link
                    href="/admin"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-2.5 p-3 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 transition-colors col-span-2"
                  >
                    <ShieldAlert className="size-4 text-rose-500 shrink-0" />
                    <span className="text-xs font-semibold text-rose-600 dark:text-rose-400">Admin Panel</span>
                  </Link>
                )}
              </div>
            </div>

            {/* Preferences & Quick Controls */}
            <div className="space-y-2 pt-2 border-t border-border/50">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground px-2">
                Preferences
              </p>

              {/* Alert Ring Control */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/40">
                <div className="flex items-center gap-2.5">
                  <Volume2 className="size-4 text-emerald-500" />
                  <div>
                    <span className="text-xs font-semibold">Sound Alert Ring</span>
                    <p className="text-[10px] text-muted-foreground">
                      Tone: {NOTIFICATION_SOUNDS.find((s) => s.id === selectedSound)?.name || selectedSound}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleRing(!alertRingEnabled)}
                  className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-colors ${
                    alertRingEnabled
                      ? 'bg-emerald-500 text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {alertRingEnabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>

              {/* Theme & Language */}
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/30 border border-border/40">
                  <span className="text-xs font-medium text-muted-foreground">Theme</span>
                  <ThemeSwitcher />
                </div>

                <div className="flex items-center justify-between p-2.5 rounded-xl bg-muted/30 border border-border/40">
                  <span className="text-xs font-medium text-muted-foreground">Lang</span>
                  <select
                    value={locale}
                    onChange={(e) => handleLanguageChange(e.target.value)}
                    className="bg-transparent text-xs font-semibold text-foreground focus:outline-none cursor-pointer"
                  >
                    <option value="en" className="dark:bg-slate-900">EN</option>
                    <option value="ur" className="dark:bg-slate-900">اردو</option>
                    <option value="pt" className="dark:bg-slate-900">PT</option>
                    <option value="es" className="dark:bg-slate-900">ES</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Sign Out Button */}
            <div className="pt-2">
              <button
                type="button"
                onClick={handleSignOut}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-destructive/10 text-destructive hover:bg-destructive/20 font-semibold text-xs transition-colors cursor-pointer"
              >
                <LogOut className="size-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <StatusModal
        open={isStatusModalOpen}
        onOpenChange={setIsStatusModalOpen}
        user={user}
      />
    </>
  );
}

export default MobileBottomNav;
